/**
 * DSA 2.0 — selección de máscara por ciclo cardíaco (ECG tags o ACF).
 */

/**
 * Lee tags cardíacos de un DataSet dicom-parser / image.data.
 * @param {object} dataSet
 */
export function readCardiacMeta(dataSet) {
  const str = (tag) => {
    try {
      if (!dataSet || typeof dataSet.string !== 'function') return ''
      return String(dataSet.string(tag) || '').trim()
    } catch (e) {
      return ''
    }
  }
  const num = (tag) => {
    const v = parseFloat(str(tag))
    return Number.isFinite(v) ? v : null
  }

  const heartRate = num('x00181088')
  const cardiacNumberOfImages = num('x00181090')
  const triggerTime = num('x00181060')
  const frameTime = num('x00181063')
  let frameTimeVector = null
  const ftv = str('x00181065')
  if (ftv) {
    frameTimeVector = ftv
      .split('\\')
      .map((x) => parseFloat(x))
      .filter((x) => Number.isFinite(x))
  }

  return {
    heartRate,
    cardiacNumberOfImages,
    triggerTime,
    frameTime,
    frameTimeVector,
  }
}

/**
 * Estima periodo en frames a partir de HR + frameTime o Cardiac Number of Images.
 * @param {{ heartRate?: number|null, cardiacNumberOfImages?: number|null, frameTime?: number|null }} meta
 * @param {number} numberOfFrames
 * @returns {number|null} frames por ciclo
 */
export function estimateCycleFrames(meta, numberOfFrames) {
  const n = Math.max(1, numberOfFrames | 0)
  if (meta && meta.cardiacNumberOfImages && meta.cardiacNumberOfImages > 1) {
    return Math.min(n, Math.max(2, Math.round(meta.cardiacNumberOfImages)))
  }
  if (meta && meta.heartRate > 0 && meta.frameTime > 0) {
    const msPerBeat = 60000 / meta.heartRate
    const frames = Math.round(msPerBeat / meta.frameTime)
    if (frames >= 2 && frames <= n) return frames
  }
  return null
}

/**
 * Sugiere índice de máscara en la misma fase cardíaca que `injectFrame`.
 * Usa el primer ciclo (frames 0..cycle-1) como pool de máscaras.
 *
 * @param {number} injectFrame
 * @param {number} cycleFrames
 * @returns {number}
 */
export function suggestMaskFromCycle(injectFrame, cycleFrames) {
  const c = Math.max(2, cycleFrames | 0)
  const inj = Math.max(0, injectFrame | 0)
  return inj % c
}

/**
 * Autocorrelación de energía de frames pre-contraste para sugerir k_mask.
 * Heurística: encuentra pico de contraste (max mean abs diff vs frame0) y
 * busca en pre-contraste el frame más correlacionado con el frame inyectado.
 *
 * @param {ArrayLike<number>[]} frameMeansOrSamples  series de medias o muestras 1D por frame
 * @param {number} injectFrame
 * @returns {{ maskIndex: number, method: 'acf', score: number }}
 */
export function suggestMaskByAcf(frameMeansOrSamples, injectFrame) {
  const n = frameMeansOrSamples.length
  if (n < 3) {
    return { maskIndex: 0, method: 'acf', score: 0 }
  }
  const inj = Math.max(0, Math.min(n - 1, injectFrame | 0))

  // Energía relativa vs frame 0
  const e0 = Number(frameMeansOrSamples[0]) || 0
  let peak = 0
  let peakE = 0
  for (let i = 1; i < n; i++) {
    const e = Math.abs((Number(frameMeansOrSamples[i]) || 0) - e0)
    if (e > peakE) {
      peakE = e
      peak = i
    }
  }

  const preEnd = Math.max(1, Math.min(inj, peak) - 1)
  const target = Number(frameMeansOrSamples[inj]) || 0
  let best = 0
  let bestScore = -Infinity
  for (let k = 0; k <= preEnd; k++) {
    const v = Number(frameMeansOrSamples[k]) || 0
    // similitud: menor |diff| y misma fase aproximada (cada ~ciclo si se conoce)
    const score = -Math.abs(v - target)
    if (score > bestScore) {
      bestScore = score
      best = k
    }
  }

  return { maskIndex: best, method: 'acf', score: bestScore }
}

/**
 * Media rápida de píxeles (submuestreo).
 * @param {TypedArray|ArrayLike<number>} pixels
 * @param {number} [stride]
 */
export function meanSubsample(pixels, stride) {
  if (!pixels || !pixels.length) return 0
  const step = Math.max(1, stride | 0 || Math.floor(pixels.length / 4096) || 1)
  let sum = 0
  let n = 0
  for (let i = 0; i < pixels.length; i += step) {
    sum += pixels[i]
    n += 1
  }
  return n ? sum / n : 0
}

/**
 * Orquesta sugerencia de máscara.
 * @param {{
 *   dataSet?: object,
 *   numberOfFrames: number,
 *   injectFrame: number,
 *   frameMeans?: number[],
 * }} input
 */
export function suggestCardiacMask(input) {
  const nFrames = Math.max(1, input.numberOfFrames | 0)
  const inject = Math.max(0, Math.min(nFrames - 1, input.injectFrame | 0))
  const meta = readCardiacMeta(input.dataSet)
  const cycle = estimateCycleFrames(meta, nFrames)

  if (cycle) {
    const maskIndex = suggestMaskFromCycle(inject, cycle)
    return {
      maskIndex,
      method: 'ecg',
      cycleFrames: cycle,
      meta,
    }
  }

  if (input.frameMeans && input.frameMeans.length >= 3) {
    const acf = suggestMaskByAcf(input.frameMeans, inject)
    return { ...acf, meta, cycleFrames: null }
  }

  return {
    maskIndex: 0,
    method: 'fallback',
    meta,
    cycleFrames: null,
  }
}
